<!--
  A WAI-ARIA tabs widget: one tablist, one tabpanel per tab, one panel
  visible at a time. Documents.vue built the first tablist in-page and said
  the extraction belonged with a second consumer; Dispatch and Billing are
  the second and third, so this is that extraction.

  The pattern, in full (https://www.w3.org/WAI/ARIA/apg/patterns/tabs/):
  role=tablist / tab / tabpanel, aria-selected, aria-controls and
  aria-labelledby tying each tab to its panel, and a roving tabindex so Tab
  moves into the list once and out again. Left/Right move between tabs and
  wrap; Home/End jump to the ends. Selection follows focus (automatic
  activation): the panels here are already rendered, so showing one costs
  nothing and there is no reason to make a keyboard user press Enter too.

  Controlled: `modelValue` is the selected tab's id and the component never
  holds a copy of it. A page that keeps the selection in the URL (Dispatch,
  Billing) owns that; this component only asks for a change.

  Panels are slots named after each tab's id and hide with v-show, not
  v-if. Every panel stays mounted, so whatever a panel fetched, filtered or
  selected survives switching away and back, and a panel that listens for
  another panel's changes keeps listening while hidden.

  A tab's `count` is optional and renders as a small badge. It is a number
  the page already has, not a reason to fetch one; null or undefined shows
  no badge at all, and 0 shows "0".
-->
<template>
  <div class="lw-c-tabs">
    <div ref="scroller" class="lw-c-tabs__list-scroller">
      <div class="lw-c-tabs__list" role="tablist" :aria-label="label" @keydown="handleKeydown">
        <button
          v-for="tab in tabs"
          :id="tabId(tab.id)"
          :key="tab.id"
          ref="tabButtons"
          type="button"
          role="tab"
          class="lw-c-tabs__tab"
          :class="{ 'lw-c-tabs__tab--selected': tab.id === modelValue }"
          :aria-selected="tab.id === modelValue ? 'true' : 'false'"
          :aria-controls="panelId(tab.id)"
          :tabindex="tab.id === modelValue ? 0 : -1"
          :data-tab="tab.id"
          @click="select(tab.id)"
        >
          <span class="lw-c-tabs__label">{{ tab.label }}</span>
          <span v-if="hasCount(tab)" class="lw-c-tabs__count">{{ tab.count }}</span>
        </button>
      </div>
    </div>

    <div
      v-for="tab in tabs"
      v-show="tab.id === modelValue"
      :id="panelId(tab.id)"
      :key="tab.id"
      role="tabpanel"
      class="lw-c-tabs__panel"
      :aria-labelledby="tabId(tab.id)"
      tabindex="0"
    >
      <slot :name="tab.id" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'LwTabs',
  props: {
    // [{ id, label, count? }]. `id` names the panel slot and is what
    // `modelValue` holds; keep it URL-safe, since pages put it in a query.
    tabs: {
      type: Array,
      required: true,
      validator: value =>
        value.length > 0 &&
        value.every(tab => tab && typeof tab.id === 'string' && typeof tab.label === 'string'),
    },
    modelValue: {
      type: String,
      required: true,
    },
    // The tablist's accessible name, e.g. "Dispatch sections".
    label: {
      type: String,
      required: true,
    },
    // Prefix for the tab and panel element ids. Two LwTabs on one page need
    // different prefixes or their ids collide.
    idPrefix: {
      type: String,
      default: 'lw-tabs',
    },
  },
  emits: ['update:modelValue'],
  watch: {
    modelValue() {
      this.$nextTick(this.revealSelected);
    },
  },
  mounted() {
    this.revealSelected();
  },
  methods: {
    // On a narrow screen the tab row scrolls sideways, and a page opened on
    // its last tab (`?tab=batch`) would otherwise show that tab cut off or
    // off screen. Moves the row's own scroll only, never the page's, which
    // scrollIntoView would also do.
    revealSelected() {
      const scroller = this.$refs.scroller;
      const buttons = this.$refs.tabButtons || [];
      const target = buttons.find(el => el.dataset.tab === this.modelValue);
      if (!scroller || !target) return;
      // offsetLeft is measured from the scroller: it is position: relative,
      // which makes it the buttons' offsetParent.
      const left = target.offsetLeft;
      const right = left + target.offsetWidth;
      if (left < scroller.scrollLeft) {
        scroller.scrollLeft = left;
      } else if (right > scroller.scrollLeft + scroller.clientWidth) {
        scroller.scrollLeft = right - scroller.clientWidth;
      }
    },
    tabId(id) {
      return `${this.idPrefix}-tab-${id}`;
    },
    panelId(id) {
      return `${this.idPrefix}-panel-${id}`;
    },
    hasCount(tab) {
      return tab.count !== null && tab.count !== undefined;
    },
    select(id) {
      if (id !== this.modelValue) this.$emit('update:modelValue', id);
    },
    handleKeydown(event) {
      const ids = this.tabs.map(tab => tab.id);
      const current = Math.max(0, ids.indexOf(this.modelValue));
      let next;
      switch (event.key) {
        case 'ArrowRight':
          next = (current + 1) % ids.length;
          break;
        case 'ArrowLeft':
          next = (current - 1 + ids.length) % ids.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = ids.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.select(ids[next]);
      // Focus moves with the selection. The button already exists, so this
      // does not wait for the parent to write the new modelValue back.
      const buttons = this.$refs.tabButtons || [];
      const target = buttons.find(el => el.dataset.tab === ids[next]);
      if (target) target.focus();
    },
  },
};
</script>

<style scoped lang="scss">
.lw-c-tabs__list-scroller {
  // Narrow widths: the tabs keep their one-line labels and the row scrolls
  // sideways instead of wrapping into a ragged second line or pushing the
  // page wider than the viewport.
  position: relative;
  overflow-x: auto;
  border-bottom: 1px solid var(--lw-border-subtle);
}

.lw-c-tabs__list {
  display: flex;
  gap: var(--lw-space-2);
  min-width: max-content;
}

.lw-c-tabs__tab {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: var(--lw-space-2);
  background: none;
  border: 0;
  // The selected tab's underline sits on the list's own hairline, so the
  // two read as one rule with a highlighted stretch.
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  padding: var(--lw-space-3) var(--lw-space-3);
  font: var(--lw-type-label);
  color: var(--lw-fg-secondary);
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: var(--lw-fg-primary);
  }

  &:focus-visible {
    outline: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
    outline-offset: calc(var(--lw-focus-ring-width) * -1);
  }
}

.lw-c-tabs__tab--selected {
  color: var(--lw-fg-primary);
  border-bottom-color: var(--lw-amber-500);
}

.lw-c-tabs__count {
  min-width: var(--lw-space-5);
  padding: 0 var(--lw-space-2);
  border-radius: var(--lw-radius-pill);
  background: var(--lw-bg-sunken);
  color: var(--lw-fg-secondary);
  font: var(--lw-type-caption-strong);
  font-variant-numeric: var(--lw-type-data-numeric);
  text-align: center;
}

.lw-c-tabs__tab--selected .lw-c-tabs__count {
  background: var(--lw-amber-100);
  color: var(--lw-amber-800);
}

.lw-c-tabs__panel {
  padding-top: var(--lw-space-6);

  &:focus-visible {
    outline: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
    outline-offset: var(--lw-focus-ring-offset);
  }
}
</style>
